import api from "@/services/apiClient"
import GtmService, { UserFlow } from "@/services/gtmService"
import { useUserStore } from "@/stores/user" // Pinia store

// Types
import UserPostIn from "@/shared/dtos/in/UserPostIn"
import { ApiError } from "@/shared/apiClient"

export function useRegisterUser() {
  const userStore = useUserStore()

  const registerUser = async (
    email?: string
  ): Promise<{
    success: boolean
    code?: "email-exists" | "user-update-failed" | "quiz-update-failed" | "user-creation-failed" | "user-load-failed"
  }> => {
    // Check if the user is already registered
    if (userStore.isLoggedIn && (userStore.isReportUser || userStore.isCoachingUser)) {
      return { success: true }
    }

    // Get data from sessionStorage
    const userData = JSON.parse(sessionStorage.getItem("userData") || "null")
    const quizData = JSON.parse(sessionStorage.getItem("quizData") || "null")

    // Update the user's data if it's available
    let userUpdated = false
    if (userData && Object.keys(userData).length > 0) {
      if (userStore.isLoggedIn) {
        // Do not replace the user's full name if it's already set
        if (userStore.getFullName) {
          delete userData.fullName
        }

        if (Object.keys(userData).length > 0) {
          try {
            await api.user.putMe(userData)
            userUpdated = true
          } catch (err) {
            console.error("Failed to update user data:", err)
            return { success: false, code: "user-update-failed" }
          }
          sessionStorage.removeItem("userData")
        }
      } else {
        const data: UserPostIn = { email, ...userData }
        try {
          await api.user.post(data)
          userUpdated = true
        } catch (err: any) {
          console.error("Failed to create user:", err)
          if (err instanceof ApiError && err.status === 403) {
            return { success: false, code: "email-exists" }
          }
          return { success: false, code: "user-creation-failed" }
        }
        sessionStorage.removeItem("userData")
      }
    }

    if (userUpdated) {
      try {
        await userStore.loadUserData()
      } catch (err) {
        console.error("Failed to load user data:", err)
        return { success: false, code: "user-load-failed" }
      }
    }

    // Update the quiz data if it's available
    if (userStore.isLoggedIn && quizData && Object.keys(quizData).length > 0) {
      try {
        await api.quiz.postReport(quizData)
        await GtmService.signUpEvent(UserFlow.REPORT)
      } catch (err) {
        console.error("Failed to update quiz data:", err)
        return { success: false, code: "quiz-update-failed" }
      }
      sessionStorage.removeItem("quizData")
    }

    return { success: true }
  }

  return registerUser
}
